﻿import { useQueryClient } from "@tanstack/solid-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatches,
  useNavigate,
  useRouterState,
} from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  lazy,
  onCleanup,
  Show,
  Suspense,
} from "solid-js";
import { BusinessSwitcher } from "~/components/business-switcher";
import { ModeToggle } from "~/components/mode-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Progress } from "~/components/ui/progress";
import { Separator } from "~/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "~/components/ui/sidebar";
import { businessesResource } from "~/features/business/business.resource";
import { authClient } from "~/lib/auth-client";
import { isAdminUser } from "~/server/admin";
import { authFunctionMiddleware } from "~/server/middleware/auth";

const getSessionUser = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => ({
    user: context.user,
    isAdmin: await isAdminUser(context.user.id),
  }));

import { IconBot, IconLoaderCircle } from "~/components/icons";
import { UploadProvider, useUpload } from "~/contexts/upload-context";
import { dashboardNav } from "~/lib/dashboard-nav";
import { cn } from "~/lib/utils";

const AgentSidebar = lazy(() =>
  import("~/components/agent/agent-sidebar").then((m) => ({
    default: m.AgentSidebar,
  })),
);

export const Route = createFileRoute("/_dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard | ActShark" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "googlebot", content: "noindex, nofollow" },
    ],
  }),
  loader: async ({ context }) => {
    try {
      const [businesses, session] = await Promise.all([
        context.queryClient.ensureQueryData(
          businessesResource.options(undefined),
        ),
        getSessionUser(),
      ]);
      return {
        businesses,
        user: session.user,
        isAdmin: session.isAdmin,
      };
    } catch {
      throw redirect({ to: "/login" });
    }
  },
  component: DashboardLayout,
});

// --- Rewritten AppSidebar to only include items/groups ---
function AppSidebarItems() {
  const routerState = useRouterState();
  const pathname = () => routerState().location.pathname;
  const { setOpenMobile } = useSidebar();
  // Read the active business from the reactive query cache (not the static
  // loader data) so nav items enable immediately when a business is created
  // or switched, without needing a hard refresh.
  const loaderData = Route.useLoaderData();
  const businesses = businessesResource.use(() => undefined);
  const hasActiveBusiness = () => !!businesses.data?.activeBusinessId;
  const isAdmin = () => !!loaderData().isAdmin;

  return (
    <SidebarContent class="gap-0.5">
      <For each={dashboardNav.navMain}>
        {(item) => {
          const visibleItems = () =>
            item.items.filter((i) => !i.adminOnly || isAdmin());
          return (
            <Show when={visibleItems().length > 0}>
              <SidebarGroup class="px-2 py-1">
                <SidebarGroupLabel class="h-7 px-2">
                  {item.title}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <For each={visibleItems()}>
                      {(navItem) => {
                        const isActive = () => pathname() === navItem.url;
                        const isDisabled = () =>
                          !!navItem.requiresBusiness && !hasActiveBusiness();

                        return (
                          <SidebarMenuItem>
                            <Show when={!navItem.adminOnly || isAdmin()}>
                              <Show
                                when={isDisabled()}
                                fallback={
                                  <Link
                                    to={navItem.url}
                                    data-sidebar="menu-button"
                                    data-size="default"
                                    data-active={isActive()}
                                    class={cn(
                                      "peer/menu-button flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
                                      isActive() && "font-medium",
                                    )}
                                    onClick={() => setOpenMobile(false)}
                                  >
                                    {navItem.icon && <navItem.icon />}
                                    <span>{navItem.title}</span>
                                  </Link>
                                }
                              >
                                <div
                                  title="Select a business first"
                                  data-sidebar="menu-button"
                                  data-size="default"
                                  class="peer/menu-button flex h-8 w-full cursor-not-allowed items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm opacity-40 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0"
                                >
                                  {navItem.icon && <navItem.icon />}
                                  <span>{navItem.title}</span>
                                </div>
                              </Show>
                            </Show>
                          </SidebarMenuItem>
                        );
                      }}
                    </For>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </Show>
          );
        }}
      </For>
    </SidebarContent>
  );
}

function SidebarBusinessSwitcher() {
  return (
    <div class="pt-0">
      <BusinessSwitcher />
    </div>
  );
}

// --- Dashboard content ---
function DashboardContent(props: {
  agentOpen: () => boolean;
  agentWidth: () => number;
  setAgentWidth: (w: number) => void;
  setAgentOpen: (open: boolean) => void;
}) {
  const loaderData = Route.useLoaderData();
  const navigate = useNavigate();
  const matches = useMatches();

  const breadcrumbs = createMemo(() =>
    matches()
      .filter((m) => m.staticData?.breadcrumb)
      .map((m, idx, arr) => {
        const isLast = idx === arr.length - 1;
        // Pathnames for layout routes do not include a trailing slash, but
        // their index child route resolves to `<path>/`.  Ensure non-current
        // breadcrumb links always target the index view.
        let href = m.pathname;
        if (!isLast && !href.endsWith("/")) {
          href = href + "/";
        }
        return {
          label:
            typeof m.staticData.breadcrumb === "function"
              ? m.staticData.breadcrumb(m)
              : (m.staticData.breadcrumb as string),
          href,
          current: isLast,
        };
      }),
  );

  const user = () => loaderData().user;

  const handleSignOut = () => {
    authClient.signOut({
      fetchOptions: { onSuccess: () => navigate({ to: "/" }) },
    });
  };

  const initials = () =>
    user()
      ?.name?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "U";

  return (
    <SidebarInset class="overflow-y-auto @container/main">
      <header class="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-backdrop-filter:bg-background/80">
        <SidebarTrigger class="-ml-1 size-9" />
        <Separator orientation="vertical" class="hidden h-4 md:block" />

        <div class="min-w-0 flex-1">
          <Breadcrumb>
            <BreadcrumbList class="flex-nowrap">
              <For each={breadcrumbs()}>
                {(breadcrumb, idx) => (
                  <>
                    <Show when={idx() > 0}>
                      <BreadcrumbSeparator class="hidden md:block shrink-0" />
                    </Show>
                    <BreadcrumbItem
                      class={
                        idx() < breadcrumbs().length - 1
                          ? "hidden md:flex"
                          : "flex-1 min-w-0"
                      }
                    >
                      <Show
                        when={breadcrumb.current}
                        fallback={
                          <Link
                            to={breadcrumb.href}
                            class="transition-colors hover:text-foreground truncate"
                          >
                            {breadcrumb.label}
                          </Link>
                        }
                      >
                        <BreadcrumbLink current class="truncate">
                          {breadcrumb.label}
                        </BreadcrumbLink>
                      </Show>
                    </BreadcrumbItem>
                  </>
                )}
              </For>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div class="flex items-center gap-1.5">
          <UploadProgressWidget />
          <Button
            variant="ghost"
            size="icon"
            class="size-9"
            title="Toggle AI Agent"
            onClick={() => props.setAgentOpen(!props.agentOpen())}
          >
            <IconBot class="size-4" />
          </Button>
          <ModeToggle />

          <Show when={user()}>
            <DropdownMenu placement="bottom-end">
              <DropdownMenuTrigger
                as={Button}
                variant="ghost"
                size="icon"
                class="size-9 rounded-full p-0"
              >
                <Avatar class="size-9 border border-border/60">
                  <AvatarImage
                    src={user()?.image ?? undefined}
                    alt={user()?.name ?? undefined}
                  />
                  <AvatarFallback>{initials()}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-56">
                <DropdownMenuLabel class="font-normal">
                  <div class="flex flex-col space-y-1">
                    <p class="text-sm font-medium leading-none">
                      {user()?.name}
                    </p>
                    <p class="text-xs leading-none text-muted-foreground">
                      {user()?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem as={Link} to="/dashboard/profile">
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem as={Link} to="/dashboard/settings/billing">
                    Billing
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Show>
        </div>
      </header>

      <Outlet />
    </SidebarInset>
  );
}

// --- Upload progress widget (inline navbar pill, matches CloudSync style) ---
function UploadProgressWidget() {
  const { uploads, removeUpload } = useUpload();

  const visible = () => uploads().length > 0;
  const allDone = () =>
    uploads().every((u) => u.status === "completed" || u.status === "error");

  // Dismiss the pill when all uploads finish (toast is fired by the upload caller)
  createEffect(() => {
    if (allDone() && uploads().length > 0) {
      uploads().forEach((u) => removeUpload(u.id));
    }
  });

  const overall = () => {
    const list = uploads();
    if (list.length === 0) return 0;

    const totalBytes = list.reduce((sum, upload) => sum + upload.size, 0);
    if (totalBytes <= 0) {
      return Math.round(
        list.reduce((sum, upload) => sum + upload.progress, 0) / list.length,
      );
    }

    const transferredBytes = list.reduce((sum, upload) => {
      const progress = Math.min(100, Math.max(0, upload.progress));
      return sum + (upload.size * progress) / 100;
    }, 0);

    return Math.round((transferredBytes / totalBytes) * 100);
  };

  const currentFile = () =>
    uploads().find((u) => u.status === "uploading")?.name ?? "Preparing...";

  return (
    <Show when={visible()}>
      <div class="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-popover w-60 h-10.5">
        <IconLoaderCircle class="size-4 animate-spin text-muted-foreground shrink-0" />
        <div class="flex-1 min-w-0 flex flex-col gap-1">
          <div class="text-xs text-muted-foreground truncate">
            {currentFile()}
          </div>
          <Progress value={overall()} minValue={0} maxValue={100} class="h-1" />
        </div>
        <div class="text-xs text-muted-foreground shrink-0 w-9 text-right">
          {overall()}%
        </div>
      </div>
    </Show>
  );
}

// --- Dashboard layout with Sidebar ---
function DashboardLayout() {
  const queryClient = useQueryClient();
  const loaderData = Route.useLoaderData();
  const [agentOpen, setAgentOpen] = createSignal(false);
  const [agentVisible, setAgentVisible] = createSignal(false);
  const [agentWidth, setAgentWidth] = createSignal(380);

  // Keep component alive for 200ms after close so CSS transition can animate out
  createEffect(() => {
    if (agentOpen()) {
      setAgentVisible(true);
    } else {
      const timer = setTimeout(() => setAgentVisible(false), 200);
      onCleanup(() => clearTimeout(timer));
    }
  });
  // Only seed if the cache is empty - prevents stale SSR from overwriting fresher client state.
  const businessesKey = businessesResource.options(undefined).queryKey;
  if (!queryClient.getQueryData(businessesKey)) {
    businessesResource.seed(queryClient, undefined, loaderData().businesses);
  }

  return (
    <UploadProvider>
      <SidebarProvider class="h-svh overflow-hidden">
        <Sidebar class="h-full">
          <SidebarHeader>
            <SidebarBusinessSwitcher />
          </SidebarHeader>

          {/* Sidebar items */}
          <AppSidebarItems />

          <SidebarRail />
        </Sidebar>

        {/* Main page content */}
        <DashboardContent
          agentOpen={agentOpen}
          agentWidth={agentWidth}
          setAgentWidth={setAgentWidth}
          setAgentOpen={setAgentOpen}
        />

        {/* Agent sidebar - sibling to SidebarInset so it pushes content */}
        <Show when={agentVisible()}>
          <Suspense>
            <AgentSidebar
              open={agentOpen()}
              onOpenChange={setAgentOpen}
              width={agentWidth}
              setWidth={setAgentWidth}
            />
          </Suspense>
        </Show>
      </SidebarProvider>
    </UploadProvider>
  );
}
